import { all, takeEvery, put, call, take } from "@redux-saga/core/effects";
import { channel } from "redux-saga";
import { AnyAction } from "redux";
import { ActionResponseType } from "../../../common/types/common.types";
import { EnumPopupType } from "../../../components/Popups/Popup.enum";
import { openPopup } from "../../../components/Popups/Popup.reducer";
import { PopupProps } from "../../../components/Popups/PopupProps.types";
import { setLoading } from "../../layouts/main-layout/MainLayout.reducer";
import {
  BULKUPLOADSTANDARDQUESTION,
  BULKUPLOADQUESTIONTYPEDROPDOWN,
  BULKUPLOADQUESTIONCATEGORYCHOICES,
  BULKUPLOADQUESTIONDROPDOWNCATEGORIES,
  SAVEBULKUPLOADQUESTIONS,
  NOTIFYSTANDARDQUESTIONISSUESBYEMAIL,
  GETCHOICESDATAONROWCLICK,
} from "./BulkUploadQuestionsPage.action";
import {
  DropDownDTO,
  QuestionChoiceDTO,
  QuestionDTO,
  StandardQuestionBulkUploadDTO,
  StandardQuestionUploadDTO,
  UploadFileDTO,
} from "../../../common/types/common.dto.types";
import {
  bulkUploadStandardQuestionApi,
  getBulkUploadQuestionCategoryChoicesApi,
  getBulkUploadQuestionDropdownCategoriesApi,
  getBulkUploadQuestionTypeForDropdownApi,
  notifyStandardQuestionIssuesByEmailApi,
  saveStandardQuestionBulkUploadApi,
} from "./BulkUploadQuestionsPage.api";
import {
  resetBulkQuestionUploadState,
  setBulkQuestionTypeList,
  setBulkUploadQuestionCategoryChoices,
  setBulkUploadQuestionDropdownCategories,
  setPageNavigation,
  setBulkQuestionUploadPageMode,
  setStandardQuestionReviewList,
  showCategoryChoicesValidationMsg,
} from "./BulkUploadQuestionsPage.reducer";
import {
  BulkQuestionUploadPageType,
  EnumBulkQuestionUploadPageMode,
} from "./BulkUploadQuestionsPage.types";
import { select } from "redux-saga/effects";
import { RootState } from "../../../core/store";
import { isObjectEqualTo } from "../../../common/functions/common.functions";
import { callAllApiRequest } from "../../../core/api/apiClient.service";

// ==============================|| VIEWS - PAGES - BULK UPLOAD QUESTIONS PAGE - SAGA ||============================== //

/**
 * A custom Redux Saga channel used for navigation-related actions.
 */
const navigationChannel = channel();

/**
 * Watches the custom navigation channel and dispatches any actions received.
 */
export function* navigationChannelWatcher() {
  while (true) {
    const action = yield take(navigationChannel);
    yield put(action);
  }
}

const bulkUploadStandardQuestionWatcher = function* (): any {
  yield takeEvery(
    BULKUPLOADSTANDARDQUESTION,
    function* (action: AnyAction): any {
      yield put(setLoading(true));

      // Extract the uploaded file payload from the dispatched action
      const uploadedStandardQuestion: UploadFileDTO = action.payload;

      try {
        // Call the API to perform the bulk upload of the standard questions
        const result: ActionResponseType = yield call(() =>
          bulkUploadStandardQuestionApi(uploadedStandardQuestion)
        );

        // If the API call is successful
        if (result.IsSuccess) {
          const questionList: StandardQuestionUploadDTO[] = result.Data;

          // Exit early if the question list is empty
          if (!questionList?.length) {
            yield put(
              openPopup({
                Open: true,
                HeaderText: "Warning",
                BodyText:
                  result?.ErrorMessage ??
                  "The uploaded file contains no questions. Please check the template and upload again.",
                PopupType: EnumPopupType.WarningPopup,
                OkBtnClick() {}, // Optional: Define if needed
              } as PopupProps)
            );

            yield put(setLoading(false));
            return;
          }

          // Dispatch the list for review
          yield put(setStandardQuestionReviewList(questionList));

          // Determine the next page mode based on presence of warnings
          const hasWarnings = questionList.some((q) => q.IsWarning ?? false);

          const bulkQuestionUploadPageMode = hasWarnings
            ? EnumBulkQuestionUploadPageMode.ValidateQuestionMode
            : EnumBulkQuestionUploadPageMode.ReviewQuestionMode;

          yield put(setBulkQuestionUploadPageMode(bulkQuestionUploadPageMode));
        } else {
          yield put(
            openPopup({
              Open: true,
              BodyText:
                result?.ErrorMessage ??
                "The uploaded file is empty or contains no valid entries. Please check and upload again.",
              HeaderText: "Warning",
              PopupType: EnumPopupType.WarningPopup,
              OkBtnClick() {},
            } as PopupProps)
          );
        }
      } catch (error) {
        yield put(
          openPopup({
            Open: true,
            BodyText:
              "The uploaded file is empty or contains no valid entries. Please check and upload again.",
            HeaderText: "Warning",
            PopupType: EnumPopupType.ErrorPopup,
          } as PopupProps)
        );
      }

      // Stop loading indicator
      yield put(setLoading(false));
    }
  );
};

const getBulkUploadQuestionTypeDropdownWatcher = function* (): any {
  yield takeEvery(
    BULKUPLOADQUESTIONTYPEDROPDOWN,
    function* (action: AnyAction): any {
      //yield put(setLoading(true));

      try {
        let result: ActionResponseType = yield call(() =>
          getBulkUploadQuestionTypeForDropdownApi()
        );

        if (result.IsSuccess) {
          let questionTypes: Array<DropDownDTO> = result.Data;

          yield put(setBulkQuestionTypeList(questionTypes));
        } else {
          let errorMessage: string = "";
          if (result.ErrorMessage && result.ErrorMessage !== "") {
            errorMessage = result.ErrorMessage;
          } else {
            errorMessage = "Failed to load Question Types. Please try again";
          }
          yield put(
            openPopup({
              Open: true,
              BodyText: errorMessage,
              HeaderText: "Error",
              PopupType: EnumPopupType.ErrorPopup,
            } as PopupProps)
          );
        }
      } catch (error) {
        yield put(
          openPopup({
            Open: true,
            BodyText: "Failed to load Question Types. Please try againn",
            HeaderText: "Error",
            PopupType: EnumPopupType.ErrorPopup,
          } as PopupProps)
        );
      }

      // yield put(setLoading(false));
    }
  );
};

const bulkUploadQuestionDropdownCategoriesWatcher = function* (): any {
  yield takeEvery(
    BULKUPLOADQUESTIONDROPDOWNCATEGORIES,
    function* (action: AnyAction): any {
      yield put(setLoading(true));

      const questionTypeId: number = action.payload?.questionTypeId;

      try {
        const result: ActionResponseType = yield call(() =>
          getBulkUploadQuestionDropdownCategoriesApi(questionTypeId)
        );

        if (result.IsSuccess) {
          const questionCategories: Array<DropDownDTO> = result.Data;

          const selectedQuestion = action.payload?.selectedStandardQuestion;
          if (selectedQuestion) {
            const {
              QuestionCategory: questionCategory,
              QuestionCategoryName: questionCategoryName,
            }: StandardQuestionUploadDTO = selectedQuestion;

            const isCategory = !questionCategory || questionCategory === 0;

            const isMatchingCategory = questionCategories?.some(
              ({ Value, Label }) =>
                Value?.toString() === String(questionCategory) &&
                Label?.toString() === String(questionCategoryName)
            );

            const isValidCategory = isCategory || isMatchingCategory;

            // show category and choises validation message
            if (!isValidCategory) {
              yield put(showCategoryChoicesValidationMsg(true));
              return;
            }
          }

          yield put(
            setBulkUploadQuestionDropdownCategories(questionCategories)
          );
        } else {
          let errorMessage: string = "";
          if (result.ErrorMessage && result.ErrorMessage !== "") {
            errorMessage = result.ErrorMessage;
          } else {
            errorMessage = "Failed to load Choices Types. Please try again";
          }
          yield put(
            openPopup({
              Open: true,
              BodyText: errorMessage,
              HeaderText: "Error",
              PopupType: EnumPopupType.ErrorPopup,
            } as PopupProps)
          );
        }
      } catch (error) {
        yield put(
          openPopup({
            Open: true,
            BodyText: "Failed to load Choices Types. Please try again",
            HeaderText: "Error",
            PopupType: EnumPopupType.ErrorPopup,
          } as PopupProps)
        );
      }

      yield put(setLoading(false));
    }
  );
};

const bulkUploadQuestionCategoryChoicesWatcher = function* (): any {
  yield takeEvery(
    BULKUPLOADQUESTIONCATEGORYCHOICES,
    function* (action: AnyAction): any {
      yield put(setLoading(true));

      const questionData: QuestionDTO = action.payload?.questionData;

      try {
        const result: ActionResponseType = yield call(() =>
          getBulkUploadQuestionCategoryChoicesApi(questionData)
        );

        if (result.IsSuccess) {
          const questionCategoryChoices: Array<QuestionChoiceDTO> = result.Data;

          const selectedQuestion = action.payload?.selectedStandardQuestion;
          if (selectedQuestion) {
            const {
              QuestionChoices: questionChoices,
            }: StandardQuestionUploadDTO = selectedQuestion;

            const selectedChoices = questionChoices?.map((choice) => ({
              ChoiceTextId: choice.ChoiceTextId,
              ChoiceText: choice.ChoiceText?.DefaultText1,
            }));

            const originalChoices = questionCategoryChoices?.map((choice) => ({
              ChoiceTextId: choice.ChoiceTextId,
              ChoiceText: choice.ChoiceText?.DefaultText1,
            }));

            const hasChoices = questionChoices && questionChoices.length > 0;
            const choicesAreEqual = isObjectEqualTo(
              selectedChoices,
              originalChoices
            );

            // show category and choises validation message
            if (!(hasChoices && choicesAreEqual)) {
              yield put(showCategoryChoicesValidationMsg(true));
              return;
            }
          }

          yield put(
            setBulkUploadQuestionCategoryChoices(questionCategoryChoices)
          );
        } else {
          const errorMessage: string =
            result.ErrorMessage ||
            "Failed to load Choices Types. Please try again";

          yield put(
            openPopup({
              Open: true,
              BodyText: errorMessage,
              HeaderText: "Error",
              PopupType: EnumPopupType.ErrorPopup,
            } as PopupProps)
          );
        }
      } catch (error) {
        yield put(
          openPopup({
            Open: true,
            BodyText: "Failed to load Choices Types. Please try again",
            HeaderText: "Error",
            PopupType: EnumPopupType.ErrorPopup,
          } as PopupProps)
        );
      }

      yield put(setLoading(false));
    }
  );
};

const saveStandardQuestionBulkUploadWatcher = function* (): any {
  yield takeEvery(SAVEBULKUPLOADQUESTIONS, function* (action: AnyAction): any {
    yield put(setLoading(true));

    const uploadedStandardQuestions: StandardQuestionBulkUploadDTO =
      action.payload;
    try {
      let result: ActionResponseType = yield call(() =>
        saveStandardQuestionBulkUploadApi(uploadedStandardQuestions)
      );

      if (result.IsSuccess) {
        yield put(
          openPopup({
            Open: true,
            BodyText:
              result.InfoMessage ||
              "Requirement(s) saved to draft successfully.",
            HeaderText: "Success",
            PopupType: EnumPopupType.SuccessPopup,
            OkBtnClick() {
              navigationChannel.put(resetBulkQuestionUploadState());
              navigationChannel.put(
                setPageNavigation({
                  EnablePageNavigation: true,
                  NavigationPage: "/standardsQuestions",
                })
              );
            },
          } as PopupProps)
        );
      } else {
        if (result?.Data?.IsStandardDeactivated) {
          yield put(
            openPopup({
              Open: true,
              BodyText:
                result?.ErrorMessage ??
                "An error occured when saving the uploaded question(s)",
              HeaderText: "Warning",
              PopupType: EnumPopupType.WarningPopup,
              OkBtnClick() {
                navigationChannel.put(resetBulkQuestionUploadState());
                navigationChannel.put(
                  setPageNavigation({
                    EnablePageNavigation: true,
                    NavigationPage: "/standards",
                  })
                );
              },
            } as PopupProps)
          );
        } else if (result?.Data?.QuestionList?.length > 0) {
          yield put(
            openPopup({
              Open: true,
              BodyText:
                "Issue(s) found while saving as draft. See the exclamation mark next to the ID for details.",
              HeaderText: "Warning",
              PopupType: EnumPopupType.WarningPopup,
              OkBtnClick() {
                const questionList: StandardQuestionUploadDTO[] =
                  result?.Data?.QuestionList;

                // Dispatch the list for review
                navigationChannel.put(
                  setStandardQuestionReviewList(questionList)
                );
              },
            } as PopupProps)
          );
        } else {
          yield put(
            openPopup({
              Open: true,
              BodyText:
                "A system error occurred while saving the uploaded question(s). Please try again.",
              HeaderText: "Error",
              PopupType: EnumPopupType.ErrorPopup,
            } as PopupProps)
          );
        }
      }
    } catch (error) {
      yield put(
        openPopup({
          Open: true,
          BodyText:
            "A system error occurred while saving the uploaded question(s). Please try again.",
          HeaderText: "Error",
          PopupType: EnumPopupType.ErrorPopup,
        } as PopupProps)
      );
    }

    yield put(setLoading(false));
  });
};

const notifyStandardQuestionIssuesByEmailWatcher = function* (): any {
  yield takeEvery(
    NOTIFYSTANDARDQUESTIONISSUESBYEMAIL,
    function* (action: AnyAction): any {
      yield put(setLoading(true));

      const uploadedStandardQuestions: StandardQuestionBulkUploadDTO =
        action.payload?.StandardQuestionBulkUploadList ?? [];

      const bulkQuestionUploadPageMode: EnumBulkQuestionUploadPageMode =
        action.payload?.BulkQuestionUploadPageMode ??
        EnumBulkQuestionUploadPageMode.UploadQuestionMode;

      const state: BulkQuestionUploadPageType = yield select(
        (s: RootState) => s.bulkQuestionUploadReducer
      );

      try {
        let result: ActionResponseType = yield call(() =>
          notifyStandardQuestionIssuesByEmailApi(uploadedStandardQuestions)
        );

        if (result.IsSuccess) {
          switch (bulkQuestionUploadPageMode) {
            case EnumBulkQuestionUploadPageMode.UploadQuestionMode: {
              // Reset state and navigate to upload page
              yield put(resetBulkQuestionUploadState());
              //yield put(setPageNavigation(true));
              break;
            }

            case EnumBulkQuestionUploadPageMode.ReviewQuestionMode: {
              // Filter out questions with warnings
              const questionList: StandardQuestionUploadDTO[] =
                state?.StandardQuestionReviewList?.filter(
                  (item) => !(item.IsWarning ?? false)
                ) || [];

              yield put(setStandardQuestionReviewList(questionList));
              yield put(
                setBulkQuestionUploadPageMode(
                  EnumBulkQuestionUploadPageMode.ReviewQuestionMode
                )
              );
              break;
            }

            default:
              break;
          }
        } else {
          yield put(
            openPopup({
              Open: true,
              BodyText:
                result?.ErrorMessage ??
                "Unable to send email notification. Please try again.",
              HeaderText: "Error",
              PopupType: EnumPopupType.ErrorPopup,
              // OkBtnClick() {
              //   navigationChannel.put(resetBulkQuestionUploadState());
              // },
            } as PopupProps)
          );
        }
      } catch (error) {
        yield put(
          openPopup({
            Open: true,
            BodyText: "Unable to send email notification. Please try again.",
            HeaderText: "Error",
            PopupType: EnumPopupType.ErrorPopup,
          } as PopupProps)
        );
      }

      yield put(setLoading(false));
    }
  );
};

const getChoicesDataOnRowClickWatcher = function* (): any {
  yield takeEvery(GETCHOICESDATAONROWCLICK, function* (action: AnyAction): any {
    yield put(setLoading(true));
    const questionTypeId: number = action.payload?.questionTypeId;
    const questionData: QuestionDTO = action.payload?.questionData;

    const getQuestionCategories: Promise<ActionResponseType> =
      getBulkUploadQuestionDropdownCategoriesApi(questionTypeId);
    const getQuestionCategoryChoices: Promise<ActionResponseType> =
      getBulkUploadQuestionCategoryChoicesApi(questionData);

    const result = yield call(
      async () =>
        await callAllApiRequest([
          getQuestionCategories,
          getQuestionCategoryChoices,
        ])
    );

    if (result[0]?.Data == null && !result[0]?.Data) {
      // TODO document why this block is empty
    } else {
      const questionCategories: Array<DropDownDTO> = result[0]?.Data;
      const questionCategoryChoices: Array<QuestionChoiceDTO> = result[1]?.Data;

      const selectedQuestion = action.payload?.selectedStandardQuestion;
      if (selectedQuestion) {
        const {
          QuestionCategory: questionCategory,
          QuestionCategoryName: questionCategoryName,
        }: StandardQuestionUploadDTO = selectedQuestion;

        const isCategory = !questionCategory || questionCategory === 0;

        const isMatchingCategory = questionCategories?.some(
          ({ Value, Label }) =>
            Value?.toString() === String(questionCategory) &&
            Label?.toString() === String(questionCategoryName)
        );

        const isValidCategory = isCategory || isMatchingCategory;

        // show category and choises validation message
        if (!isValidCategory) {
          yield put(showCategoryChoicesValidationMsg(true));
          return;
        }
      }

      //Set the question category choices

      if (selectedQuestion) {
        const { QuestionChoices: questionChoices }: StandardQuestionUploadDTO =
          selectedQuestion;

        const selectedChoices = questionChoices?.map((choice) => ({
          ChoiceTextId: choice.ChoiceTextId,
          ChoiceText: choice.ChoiceText?.DefaultText1,
        }));

        const originalChoices = questionCategoryChoices?.map((choice) => ({
          ChoiceTextId: choice.ChoiceTextId,
          ChoiceText: choice.ChoiceText?.DefaultText1,
        }));

        const hasChoices = questionChoices && questionChoices.length > 0;
        const choicesAreEqual = isObjectEqualTo(
          selectedChoices,
          originalChoices
        );

        // show category and choises validation message
        if (!(hasChoices && choicesAreEqual)) {
          yield put(showCategoryChoicesValidationMsg(true));
          return;
        }
      }

      yield put(setBulkUploadQuestionDropdownCategories(questionCategories));
      yield put(setBulkUploadQuestionCategoryChoices(questionCategoryChoices));
    }

    yield put(setLoading(false));
  });
};

const bulkUploadStandardQuestionSaga = function* () {
  yield all([
    bulkUploadStandardQuestionWatcher(),
    getBulkUploadQuestionTypeDropdownWatcher(),
    bulkUploadQuestionDropdownCategoriesWatcher(),
    bulkUploadQuestionCategoryChoicesWatcher(),
    saveStandardQuestionBulkUploadWatcher(),
    navigationChannelWatcher(),
    notifyStandardQuestionIssuesByEmailWatcher(),
    getChoicesDataOnRowClickWatcher(),
  ]);
};

export default bulkUploadStandardQuestionSaga;
